/**
 * Cart-value threshold freebie (separate from subtotal-interval / gift-with-purchase flows).
 * Line items use property _cart_value_auto_freebie = true.
 */
(function () {
  const SOURCE = 'cart-value-threshold-freebie';
  const PROP = '_cart_value_auto_freebie';

  function localePrefix() {
    const seg = window.location.pathname.split('/')[1];
    return ['en', 'ar'].includes(seg) ? `/${seg}` : '';
  }

  function parseThresholdMinor(cfg) {
    const raw = String((cfg && cfg.threshold) || '').replace(/,/g, '').trim();
    const n = parseFloat(raw);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.round(n * 100);
  }

  function isCartValueAutoLine(item) {
    const p = item.properties || {};
    return String(p[PROP] || '').toLowerCase() === 'true';
  }

  function isHeavyDiscountLine(item) {
    const props = item.properties || {};
    if (props['_equalorabove50%discount'] === 'true') return true;
    const orig = Number(item.original_line_price || 0);
    const fin = Number(
      item.final_line_price != null ? item.final_line_price : item.original_line_price || 0
    );
    if (orig <= 0) return false;
    return fin / orig <= 0.5;
  }

  function eligibleSubtotalCents(cart) {
    let sum = 0;
    for (const item of cart.items || []) {
      if (isCartValueAutoLine(item)) continue;
      if (isHeavyDiscountLine(item)) continue;
      sum += Number(item.final_line_price || 0);
    }
    return sum;
  }

  function matchingLines(cart, variantKey) {
    return (cart.items || []).filter((item) => {
      const p = item.properties || {};
      return String(item.variant_id) === variantKey && String(p[PROP] || '').toLowerCase() === 'true';
    });
  }

  async function refreshDrawer(locale, cart) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') return;
    const sectionRes = await fetch(`${locale}/?sections=cart-drawer,cart-icon-bubble`, {
      credentials: 'same-origin',
    });
    const sectionJson = await sectionRes.json();
    const sections = sectionJson.sections || sectionJson;
    const firstKey = (cart.items && cart.items[0] && cart.items[0].key) || cart.token || '';
    cartDrawer.renderContents({ id: firstKey, sections });
  }

  function syncEmptyChrome(cart) {
    const empty = Number(cart.item_count || 0) === 0;
    const drawer = document.querySelector('cart-drawer');
    const footer = document.getElementById('main-cart-footer');
    if (drawer) drawer.classList.toggle('is-empty', empty);
    if (footer) footer.classList.toggle('is-empty', empty);
    document.querySelector('.custom-isempty')?.classList.toggle('is-empty', empty);
    document.querySelector('.drawer__header')?.classList.toggle('hidden', empty);
  }

  async function syncCartValueFreebie() {
    const cfg = window.cartValueFreebieConfig;
    if (!cfg || cfg.variantId == null || cfg.variantId === '') {
      window.__cartValueFreebieSyncScheduled = false;
      window.__cartValueFreebieBusy = false;
      if (typeof refreshDrawerCheckoutButtonsState === 'function') refreshDrawerCheckoutButtonsState();
      return;
    }
    if (window.__cartValueFreebieBusy) {
      window.__cartValueFreebieSyncScheduled = false;
      if (typeof refreshDrawerCheckoutButtonsState === 'function') refreshDrawerCheckoutButtonsState();
      return;
    }

    const locale = localePrefix();
    const variantKey = String(cfg.variantId);
    const thresholdMinor = parseThresholdMinor(cfg);

    try {
      window.__cartValueFreebieBusy = true;
      window.__cartValueFreebieSyncScheduled = false;
      if (typeof refreshDrawerCheckoutButtonsState === 'function') refreshDrawerCheckoutButtonsState();

      const cart = await fetch(`${locale}/cart.js`, { credentials: 'same-origin' }).then((r) => r.json());
      const lines = matchingLines(cart, variantKey);

      if (!cfg.enabled) {
        let didUpdate = false;
        if (lines.length) {
          const updates = {};
          lines.forEach((line) => {
            updates[line.key] = 0;
          });
          const res = await fetch(`${locale}/cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ updates }),
          });
          didUpdate = res.ok;
        }
        if (didUpdate) {
          const fresh = await fetch(`${locale}/cart.js`, { credentials: 'same-origin' }).then((r) => r.json());
          await refreshDrawer(locale, fresh);
          syncEmptyChrome(fresh);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: fresh });
          }
        }
        return;
      }

      const eligible = eligibleSubtotalCents(cart);
      const wantLine = thresholdMinor > 0 && eligible >= thresholdMinor;

      let didUpdate = false;

      if (!wantLine && lines.length) {
        const updates = {};
        lines.forEach((line) => {
          updates[line.key] = 0;
        });
        const res = await fetch(`${locale}/cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates }),
        });
        didUpdate = res.ok;
      } else if (wantLine) {
        if (lines.length > 1) {
          const updates = {};
          lines.forEach((line, idx) => {
            updates[line.key] = idx === 0 ? 1 : 0;
          });
          const res = await fetch(`${locale}/cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ updates }),
          });
          didUpdate = res.ok;
        } else if (lines.length === 1) {
          const line = lines[0];
          if (Number(line.quantity) !== 1) {
            const res = await fetch(`${locale}/cart/change.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({ id: line.key, quantity: 1 }),
            });
            const j = await res.json().catch(() => ({}));
            didUpdate = Boolean(res.ok && !j.errors);
          }
        } else {
          const fd = new FormData();
          fd.append('id', variantKey);
          fd.append('quantity', '1');
          fd.append(`properties[${PROP}]`, 'true');
          const res = await fetch(`${locale}/cart/add.js`, {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            credentials: 'same-origin',
            body: fd,
          });
          const j = await res.json().catch(() => ({}));
          didUpdate = Boolean(res.ok && j && !j.status);
        }
      }

      if (!didUpdate) return;

      const fresh = await fetch(`${locale}/cart.js`, { credentials: 'same-origin' }).then((r) => r.json());
      await refreshDrawer(locale, fresh);
      syncEmptyChrome(fresh);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: fresh });
      }
    } catch (e) {
      console.error('[Cart value freebie]', e);
    } finally {
      window.__cartValueFreebieBusy = false;
      if (typeof refreshDrawerCheckoutButtonsState === 'function') refreshDrawerCheckoutButtonsState();
    }
  }

  function scheduleCartValueFreebieSync() {
    clearTimeout(window.__cartValueFreebieDebounceTimer);
    window.__cartValueFreebieSyncScheduled = true;
    if (typeof refreshDrawerCheckoutButtonsState === 'function') refreshDrawerCheckoutButtonsState();
    window.__cartValueFreebieDebounceTimer = setTimeout(syncCartValueFreebie, 420);
  }

  function attachSubscriber() {
    if (window.__cartValueFreebieSubscribed) return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      if ((window.__cvFbSubTries = (window.__cvFbSubTries || 0) + 1) <= 35) {
        setTimeout(attachSubscriber, 100);
      }
      return;
    }
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === SOURCE) return;
      scheduleCartValueFreebieSync();
    });
    window.__cartValueFreebieSubscribed = true;
  }

  function attachPdpClick() {
    if (window.__cartValueFreebiePdpAttached) return;
    document.addEventListener(
      'click',
      (e) => {
        if (!e.target.closest('[name="add"]')) return;
        setTimeout(scheduleCartValueFreebieSync, 450);
        setTimeout(scheduleCartValueFreebieSync, 1000);
      },
      true
    );
    window.__cartValueFreebiePdpAttached = true;
  }

  function patchUpsell() {
    if (window.__cartValueFreebieUpsellPatched) return;
    if (typeof window.upsellAddCart !== 'function') {
      if ((window.__cvFbUpsellTries = (window.__cvFbUpsellTries || 0) + 1) <= 45) {
        setTimeout(patchUpsell, 150);
      }
      return;
    }
    window.__cartValueFreebieUpsellPatched = true;
    const prev = window.upsellAddCart;
    window.upsellAddCart = async function wrappedUpsell(event) {
      try {
        await prev.call(this, event);
      } finally {
        scheduleCartValueFreebieSync();
        setTimeout(scheduleCartValueFreebieSync, 500);
      }
    };
  }

  window.scheduleCartValueFreebieSync = scheduleCartValueFreebieSync;
  window.syncCartValueFreebie = syncCartValueFreebie;

  function init() {
    attachSubscriber();
    attachPdpClick();
    patchUpsell();
    scheduleCartValueFreebieSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
